import type { AppSettings } from '../types';

/**
 * What the app showed when these values were hardcoded. Fallback only: the live
 * settings come from `app_settings` and are cached on the device, so a fresh
 * install reads correctly before its first sync.
 */
export const FALLBACK_SETTINGS: AppSettings = {
  brandName: 'Luck Family',
  brandTagline: 'MANAGER APP',
  address: '',
  phone: '',
  gstin: '',
  discountPresets: [10, 20],
  maxDiscountPct: 90,
};

/** Database key -> the field it fills. Keys the app does not know are ignored. */
const KEY_MAP: Record<string, keyof AppSettings> = {
  brand_name: 'brandName',
  brand_tagline: 'brandTagline',
  address: 'address',
  phone: 'phone',
  gstin: 'gstin',
  discount_presets: 'discountPresets',
  max_discount_pct: 'maxDiscountPct',
};

export const SETTING_KEYS = KEY_MAP;

/** The database key for a field, for writing one setting back. */
export function settingKeyFor(field: keyof AppSettings): string {
  const found = Object.entries(KEY_MAP).find(([, f]) => f === field);
  return found ? found[0] : field;
}

/**
 * Folds `app_settings` rows onto the defaults.
 *
 * Every value is type-checked rather than trusted: these rows are edited by
 * hand in the Supabase dashboard, and one bad row must not be able to blank the
 * restaurant's name or make every discount button disappear.
 */
export function settingsFromRows(
  rows: { key: string; value: unknown }[],
): AppSettings {
  const out: AppSettings = { ...FALLBACK_SETTINGS };

  for (const row of rows) {
    const field = KEY_MAP[row.key];
    if (!field) continue;
    const v = row.value;

    if (field === 'discountPresets') {
      const list = Array.isArray(v)
        ? v.map(Number).filter((n) => Number.isFinite(n) && n > 0 && n <= 100)
        : [];
      if (list.length) out.discountPresets = list;
    } else if (field === 'maxDiscountPct') {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0 && n <= 100) out.maxDiscountPct = Math.trunc(n);
    } else if (typeof v === 'string') {
      // An empty address or phone is meaningful — it means "not set yet" — but
      // an empty brand name would leave the sidebar blank, so that one falls back.
      if (v.trim() || (field !== 'brandName' && field !== 'brandTagline')) {
        out[field] = v;
      }
    }
  }

  return out;
}

/** The rows to write back for a whole settings object. */
export function settingsToRows(s: AppSettings): { key: string; value: unknown }[] {
  return Object.entries(KEY_MAP).map(([key, field]) => ({ key, value: s[field] }));
}
